import { Drawer } from "@mui/material";

import { DrawerContent } from "components/atoms/drawer/DrawerContent";
import { DrawerFooter } from "components/atoms/drawer/DrawerFooter";
import { DrawerHeader } from "components/atoms/drawer/DrawerHeader";
import { DrawerWrapper } from "components/atoms/drawer/DrawerWrapper";
import { GET_IDENTITY_WITH_AFFILIATIONS } from "components/organisms/DashboardMenu";
import { graphql } from "gql";
import {
  DisinfectionFilter,
  DisinfectionFilterOptions,
  DisinfectionModes,
  FilterType,
  InterruptionReason,
} from "gql/graphql";
import _ from "lodash";
import { useEffect } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { hasSupportRole, isSupportPath } from "utils/privileges/privilegeUtils";
import { useQueryWithSnack } from "utils/useQueryWithSnack";
import { getSelectedFilterList } from "utils/UVD/getSelectedFilterList";
import { getFilterComponent } from "./getFilterComponent";
import { SelectedFilterChips } from "./SelectedFilterChips";

interface Props {
  open: boolean;
  onClose: () => void;
  hiddenFilter?: DisinfectionFilter;
}

const QUERY = graphql(`
  query DisinfectionFilterDrawer($filter: DisinfectionFilter!) {
    numberOfDisinfections(filter: $filter)
    filterOptions {
      serialNumbers
      rooms
      departments
      operators
      disinfectionModes
      disinfectionStatus
    }
  }
`);

const QUERY_NO_ROOMS_DEPARTMENTS = graphql(`
  query DisinfectionFilterDrawerNoRoomsDepartments(
    $filter: DisinfectionFilter!
  ) {
    numberOfDisinfections(filter: $filter)
    filterOptions {
      serialNumbers
      operators
      disinfectionModes
      disinfectionStatus
    }
  }
`);

const SUPPORT_FILTEROPTIONS_QUERY = graphql(`
  query DisinfectionFilterDrawerSupportOptions {
    filterOptions {
      organizations {
        id
        name
      }
      organizationCountries
      robotTypes
      interruptionReasons
    }
  }
`);

type DisplayFilters = Array<keyof DisinfectionFilterOptions>;

const supportFilters: DisplayFilters = [
  "serialNumbers",
  "disinfectionStatus",
  "interruptionReasons",
  "organizations",
  // "departments",
  // "rooms",
  "operators",
  // "robotTypes",
  "disinfectionModes",
  "organizationCountries",
];

const defaultFilters: DisplayFilters = [
  "disinfectionStatus",
  "departments",
  "rooms",
  "operators",
  "serialNumbers",
  "disinfectionModes",
];

export function useDDFilter() {
  const [searchParams, setSearchParams] = useSearchParams();

  const fromUrl = _.first(searchParams.getAll("filters"));

  const endDate = new Date();
  endDate.setHours(23, 59, 59, 999);
  const startDate = new Date();
  startDate.setMonth(startDate.getMonth() - 1);
  startDate.setHours(0, 0, 0, 0);
  const defaultFilter = {
    timeSpan: { start: startDate.toISOString(), end: endDate.toISOString() },
  };

  const setDDFilter = (obj: any) => {
    if (_.isEmpty(obj)) {
      searchParams.delete("filters");
    } else {
      const formattedObj = {
        ...obj,
        timeSpan: obj.timeSpan && {
          start: new Date(obj.timeSpan.start).toISOString(),
          end: new Date(obj.timeSpan.end).toISOString(),
        },
      };
      searchParams.set("filters", JSON.stringify(formattedObj));
    }
    setSearchParams(searchParams);
  };

  useEffect(() => {
    const fromUrlObj = fromUrl ? JSON.parse(fromUrl) : {};
    if (!fromUrl || !fromUrlObj.timeSpan.start || !fromUrlObj.timeSpan.end) {
      // Start & end of time span are mandatory, use default if not already present
      const mergedFilters = { ...defaultFilter, ...fromUrlObj };
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set("filters", JSON.stringify(mergedFilters));
      setSearchParams(newSearchParams, { replace: true });
    }
  }, [fromUrl, searchParams, setSearchParams]);

  const ddfilter =
    fromUrl && !_.isEmpty(JSON.parse(fromUrl))
      ? JSON.parse(fromUrl)
      : defaultFilter;

  if (ddfilter.rooms) {
    let defaultRoomSelected = false;
    ddfilter.rooms = ddfilter.rooms.map((item: any) => {
      if (item === "Current location") {
        defaultRoomSelected = true;
        return "";
      }
      return item;
    });
    if (defaultRoomSelected) {
      // Backend treats these separately, frontend pretends they are the same
      ddfilter.rooms.push("Current location");
    }
  }
  if (ddfilter.departments) {
    ddfilter.departments = ddfilter.departments.map((item: any) => {
      if (item === "No Department") {
        return "";
      }
      return item;
    });
  }

  return [ddfilter, setDDFilter];
}

export const DisinfectionFilterPanel = ({
  open,
  onClose,
  hiddenFilter,
}: Props) => {
  const location = useLocation();
  const [DDFilters, setDDFilter] = useDDFilter();

  const { data: identityData } = useQueryWithSnack(
    GET_IDENTITY_WITH_AFFILIATIONS,
    {
      fetchPolicy: "cache-first",
    },
  );

  const isSupporter = hasSupportRole(identityData?.me);
  const isOnSupportPath = isSupportPath(location);

  const { data: supportFilterOptionsData } = useQueryWithSnack(
    SUPPORT_FILTEROPTIONS_QUERY,
    {
      skip: !isSupporter || !isOnSupportPath,
    },
  );

  const filterList = getSelectedFilterList(DDFilters).map((item: any) => {
    if (item.label === "organizationIds") {
      // Filterlist should contain name of orgs also
      return {
        label: item.label,
        value: item.value.map((el: any) => {
          return {
            id: el,
            name: supportFilterOptionsData?.filterOptions?.organizations?.find(
              (o: any) => o.id === el,
            )?.name,
          };
        }),
      };
    } else {
      return item;
    }
  });
  const filterListLabels = filterList.map((item: any) => item.label);

  const { data, loading, error } = useQueryWithSnack(
    isOnSupportPath ? QUERY_NO_ROOMS_DEPARTMENTS : QUERY,
    {
      variables: {
        filter: {
          ...DDFilters,
          ...hiddenFilter,
        },
      },
    },
  );

  const handleResetFilter = () => {
    const { timeSpan } = DDFilters;
    setDDFilter({ timeSpan });
  };

  const getDisinfectionModes = (
    prev: Array<DisinfectionModes>,
    selectedValue: DisinfectionModes,
  ) => {
    if (prev.includes(selectedValue)) {
      return prev?.filter((element) => element != selectedValue);
    } else {
      return prev.concat([selectedValue]);
    }
  };

  const handleChange = (value: any, id: string, checked?: boolean) => {
    const old = DDFilters;
    if (id === "disinfectionModes") {
      const newModes = getDisinfectionModes(old.disinfectionModes || [], value);

      if (checked == false) {
        const elementsLeft = DDFilters.disinfectionModes.filter(
          (item: DisinfectionModes) => item !== value,
        );
        if (elementsLeft.length === 0) {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { [id]: _, ...rest } = DDFilters;

          setDDFilter(rest);
        } else {
          setDDFilter({
            ...old,
            disinfectionModes: elementsLeft,
          });
        }
      } else {
        setDDFilter({ ...old, [id]: newModes });
      }
    } else if (id === "interruptionReasons") {
      if (value.length === 0) {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { interruption, ...rest } = DDFilters;
        setDDFilter(rest);
      } else {
        setDDFilter({
          ...old,
          interruption: {
            reasons: value as InterruptionReason[],
            type: FilterType.Inclusive,
          },
        });
      }
    } else if (id === "organizationIds") {
      if (value.length === 0) {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { organizationIds, ...rest } = DDFilters;
        setDDFilter(rest);
      } else {
        setDDFilter({
          ...old,
          organizationIds: value.map((item: any) => item.id),
        });
      }
    } else {
      if (value.length === 0) {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { [id]: _, ...rest } = DDFilters;

        setDDFilter(rest);
      } else {
        setDDFilter({ ...old, [id]: value });
      }
    }
  };

  const removeSelectedFilter = (value: any, id: keyof DisinfectionFilter) => {
    const values: any = DDFilters[id];

    let newArr: any =
      values instanceof Array
        ? values.filter((item: any) => item !== value)
        : [];

    if (id === "interruption") {
      const reasons = DDFilters.interruption?.reasons.filter(
        (item: any) => item !== value,
      );

      if (reasons.length === 0) {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { interruption, ...rest } = DDFilters;
        return setDDFilter(rest);
      }

      return setDDFilter(
        (reasons || []).length > 0
          ? {
              ...DDFilters,
              interruption: {
                type: DDFilters.interruption?.type,
                reasons,
              } as any,
            }
          : DDFilters,
      );
    }

    if (id === "organizationIds") {
      return setDDFilter({
        ...DDFilters,
        organizationIds: Object.values(DDFilters.organizationIds).filter(
          (orgId) => orgId !== value.id,
        ),
      });
    }

    if (id === "disinfectionStatus" && newArr.length === 0) {
      return setDDFilter({
        ...DDFilters,
        disinfectionStatus: undefined,
      });
    }

    if (value === "Current location") {
      // Room filter "Current location" will always coexist with room filter ""
      // Handle this special case when removing the filter
      newArr = newArr.filter((item: any) => item !== "");
    }

    if (newArr.length === 0) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { [id]: _, ...rest } = DDFilters;
      return setDDFilter(rest);
    }

    return setDDFilter({
      ...DDFilters,
      ...{ [id]: newArr },
    });
  };

  return (
    <Drawer
      sx={{
        minWidth: 500,
      }}
      anchor={"right"}
      open={open}
      onClose={onClose}
    >
      <DrawerWrapper>
        <DrawerHeader header="Filter" onClose={onClose} />
        <SelectedFilterChips
          filterList={filterList}
          removeSelectedFilter={removeSelectedFilter}
        />
        <DrawerContent
          hideFooter={filterList.length === 0}
          sx={{ paddingTop: 0 }}
        >
          {(isSupporter && isOnSupportPath
            ? supportFilters
            : defaultFilters
          ).map((item) =>
            getFilterComponent({
              id: item,
              filterListLabels,
              filterOptions: data?.filterOptions,
              supportFilterOptions: supportFilterOptionsData?.filterOptions,
              onChange: handleChange,
              filterList,
            }),
          )}
        </DrawerContent>

        {filterList.length === 0 ? null : (
          <DrawerFooter
            overrideWarning
            error={error ? "Filter query failed - try again." : undefined}
            action={{
              label: `Show ${data?.numberOfDisinfections ?? 0} disinfections`,
              onClick: onClose,
              loading: loading,
            }}
            secondaryAction={{
              label: "Reset filter",
              onClick: handleResetFilter,
              color: "primary",
            }}
          />
        )}
      </DrawerWrapper>
    </Drawer>
  );
};
